import { LogOut } from 'lucide-react';
import type { OfficerIdentityDto } from '@pams/shared';
import { OfficerAvatar } from '@/components/OfficerAvatar';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';

/**
 * WHO IS SIGNED IN AND WHERE THEY SIT. Always rendered, never in a popup.
 *
 * ⚠️ THIS IS A TEST CONTRACT, and the reason it is not folded into the avatar menu.
 * `AppShell.test.tsx` asserts `getByText('Test Officer')` and `getByText('Insp. · KP')`.
 * An AntD `Dropdown` renders its overlay lazily, so identity inside the menu is identity
 * that does not exist until someone clicks — and both assertions would fail.
 *
 * It is also the better design. A nodal's whole mental model of a filtered list is "what
 * is my scope", and the answer belongs on screen rather than one click away. The avatar
 * menu therefore shows the phone and the access role and NEVER repeats the name, so
 * "identity renders exactly once" holds whether the menu is open or closed.
 *
 * The rank/unit expression is copied verbatim from the P1a header so the two text nodes
 * concatenate identically.
 *
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [amended PRE-P6 §5] SIGN OUT IS HERE TOO, AND THE OLD ONE IS NOT REMOVED.
 *
 *  There has been a `Sign out` since [P1a.2] — in `AvatarMenu`, which is mounted in the
 *  UTILITY BAR at the top of the content column, not in the sidebar. This block is the
 *  sidebar's user area and it carried no control at all, so an operator looking at their own
 *  name and avatar had nothing to click. Two entry points to one action is not duplication
 *  here: it is the action being where the identity is, which is where people look for it.
 *
 *  ⚠️ A PLAIN BUTTON, NEVER A `Dropdown`. A new overlay would have to be registered in
 *  `e2e/fixtures/overlays.ts` or `census.spec.ts` fails — correctly — and it would buy
 *  nothing: there is exactly one action, and putting one action behind a menu is a click for
 *  its own sake. `AvatarMenu` keeps its item because it also carries the phone and the role.
 *
 *  ⚠️ IT PRINTS NEITHER THE NAME NOR THE RANK LINE. `AppShellInvariants.test.tsx` asserts
 *  `getAllByText('Test Officer')` and `getAllByText('Insp. · KP')` are each length ONE, and
 *  a second copy of either would break both — the same trap the header above records for
 *  folding this block into the menu.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const OfficerIdentity = ({ officer, collapsed }: { officer: OfficerIdentityDto; collapsed?: boolean }) => {
  const { signOut } = useAuth();

  return (
    <div
      className={cn(
        'flex items-center gap-2 border-t border-border p-3',
        collapsed && 'flex-col justify-center gap-1 px-0',
      )}
    >
      <OfficerAvatar name={officer.name} />
      <div className={cn('min-w-0 flex-1', collapsed && 'sr-only')}>
        <div className="truncate text-sm font-medium leading-tight">{officer.name}</div>
        <div className="truncate text-xs text-muted-foreground">
          {officer.rank?.shortName ?? '—'} · {officer.orgUnit?.shortCode ?? '—'}
        </div>
      </div>
      {/*
        ⚠️ AN `aria-label` AND NO TOOLTIP, WHICH IS THIS FILE'S NEIGHBOUR'S CHOICE RATHER THAN
        MINE. `AppSidebar` has no `Tooltip` anywhere — its collapsed nav items carry `sr-only`
        text instead — and adding the first one here would be a new overlay that
        `e2e/specs/census.spec.ts` requires registering in `e2e/fixtures/overlays.ts`, with a
        sweep that has to collapse the sidebar to reach it. For one icon whose accessible name
        is already right, that is cost with no coverage.

        ⚠️ THE LABEL IS THE SAME AT BOTH WIDTHS, so `getByRole('button', { name: 'Sign out' })`
        resolves to one element whether the rail is open or collapsed.
      */}
      <button
        type="button"
        aria-label={SIGN_OUT}
        title={SIGN_OUT}
        data-testid="sidebar-sign-out"
        onClick={() => void signOut()}
        className={cn(
          'inline-flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground',
          'transition-colors hover:bg-accent hover:text-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        )}
      >
        <LogOut className="size-4" aria-hidden />
      </button>
    </div>
  );
};

/** One string, so the tooltip, the label and the test all say the same thing. */
const SIGN_OUT = 'Sign out';
